import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import Lenis from 'lenis';
import 'lenis/dist/lenis.css';
import { gsap, ScrollTrigger } from './gsap';
import { prefersReducedMotion } from './useReducedMotion';

const LenisContext = createContext<Lenis | null>(null);

/** The active Lenis instance, or null (reduced motion, other routes, first render). */
export function useLenis(): Lenis | null {
	return useContext(LenisContext);
}

/**
 * Lenis smooth scroll, driven by the GSAP ticker so ScrollTrigger and Lenis
 * share one clock. Disabled entirely under prefers-reduced-motion.
 */
export function SmoothScroll({ children }: { children: ReactNode }) {
	const [lenis, setLenis] = useState<Lenis | null>(null);

	useEffect(() => {
		if (prefersReducedMotion()) return;

		const instance = new Lenis({
			duration: 1.15,
			easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
			smoothWheel: true,
		});
		instance.on('scroll', ScrollTrigger.update);

		const tick = (time: number) => instance.raf(time * 1000);
		gsap.ticker.add(tick);
		gsap.ticker.lagSmoothing(0);
		setLenis(instance);

		return () => {
			gsap.ticker.remove(tick);
			gsap.ticker.lagSmoothing(500, 33);
			// Lenis does not clear its velocity-reset timeout on destroy: when it fires
			// (up to 400 ms later) it re-adds its classes to <html> on the next page.
			const pending = (instance as unknown as { _resetVelocityTimeout?: ReturnType<typeof setTimeout> | null })
				._resetVelocityTimeout;
			if (pending) clearTimeout(pending);
			instance.destroy();
			setLenis(null);
		};
	}, []);

	return <LenisContext.Provider value={lenis}>{children}</LenisContext.Provider>;
}
